/* eslint-disable max-len */
/**
 * @file 系统管理路由
 * @author gaoxunchen
 */

import _ from 'lodash';
import React from 'react';
import { Loading } from '@tencent/ptp-fe-common';
import loadable from '@loadable/component';

import perms from '@/common/permissions/sale/perms';
import { isOa } from '@/utils';

import { oneOf } from './utils';

export default [
  {
    label: '系统管理',
    href: '/sysadmin/agencies',
    key: 'sysadmin',
    path: '/sysadmin/:type',
    perm: (loginStatus: Record<string, any>) => loginStatus.hasSaleRole && loginStatus.oneOf([...perms.all_sysadmin, ...perms.all_materials]) && loginStatus.isOa,
    data: [
      {
        label: '系统管理',
        data: [
          {
            label: '系统管理首页',
            href: '/homepage',
            key: 'homepage',
            component: loadable(() => import(/* webpackChunkName: 'sysadmin' */ /* webpackMode: 'lazy' */ '@/pages/sysadmin/HomePage'), { fallback: <Loading /> }),
            hide: true,
          },
          {
            label: '服务商管理',
            href: '/sysadmin/agencies',
            key: 'sysadmin-agencies',
            perm: oneOf(perms.GLOBAL_AGENCY_MANAGE),
            component: loadable(() => import(/* webpackChunkName: 'sysadmin' */ /* webpackMode: 'lazy' */ '@/pages/sysadmin'), { fallback: <Loading /> }),
          },
          {
            label: '渠道经理管理',
            href: '/sysadmin/channel',
            key: 'sysadmin-channel',
            perm: oneOf(perms.GLOBAL_CHANNEL_MANAGER_MANAGE),
            component: loadable(() => import(/* webpackChunkName: 'sysadmin' */ /* webpackMode: 'lazy' */ '@/pages/sysadmin'), { fallback: <Loading /> }),
          },
          {
            label: '服务商业绩管理',
            href: '/target/agency',
            key: 'sysadmin-target',
            showSideNav: false,
            data: [{
              label: '订单业绩管理',
              href: '/target/agency',
              key: 'material-order-list',
              component: loadable(() => import(/* webpackChunkName: 'sysadmin' */ /* webpackMode: 'lazy' */ '@/pages/saleTarget/modules/agencyTarget'), { fallback: <Loading /> }),
              basePage: true,
              showSideNav: false,
              perm: (loginStatus: Record<string, any>) => {
                return isOa() && loginStatus.oneOf([perms.PERM_SALES_PERFORMANCE_TARGET_OA_VIEW_SELF, perms.PERM_SALES_PERFORMANCE_TARGET_OA_VIEW_ALL]);
              },
              data: [
                {
                  label: '修改目标',
                  href: '/target/agency/edit',
                  component: loadable(() => import(/* webpackChunkName: 'sysadmin' */ /* webpackMode: 'lazy' */ '@/pages/saleTarget/modules/agencyTargetEdit'), { fallback: <Loading /> }),
                  perm: (loginStatus: Record<string, any>) => {
                    return isOa() && loginStatus.oneOf([perms.PERM_SALES_PERFORMANCE_TARGET_OA_SET]);
                  },
                  data: [],
                },
              ],
            }, {
              label: '有效新客业绩管理',
              href: '/target/agency/effective',
              key: 'material-effective-list',
              component: loadable(() => import(/* webpackChunkName: 'sysadmin' */ /* webpackMode: 'lazy' */ '@/pages/saleTarget/modules/effective'), { fallback: <Loading /> }),
              basePage: true,
              showSideNav: false,
              perm: (loginStatus: Record<string, any>) => {
                return isOa() && loginStatus.oneOf([perms.PERM_VALID_NEW_CUSTOMER_TARGET_OA_VIEW_SELF, perms.PERM_VALID_NEW_CUSTOMER_TARGET_OA_VIEW_ALL]);
              },
              data: [
                {
                  label: '修改目标',
                  href: '/target/agency/effective/edit',
                  component: loadable(() => import(/* webpackChunkName: 'sysadmin' */ /* webpackMode: 'lazy' */ '@/pages/saleTarget/modules/effectiveEdit'), { fallback: <Loading /> }),
                  perm: (loginStatus: Record<string, any>) => {
                    return isOa() && loginStatus.oneOf([perms.PERM_VALID_NEW_CUSTOMER_TARGET_OA_SET]);
                  },
                  data: [],
                },
              ],
            }],
          },
          {
            label: '规则管理',
            href: '/sysadmin/rules',
            key: 'sysadmin-rules',
            perm: oneOf(perms.GLOBAL_RULE_MANAGE),
            component: loadable(() => import(/* webpackChunkName: 'sysadmin' */ /* webpackMode: 'lazy' */ '@/pages/sysadmin'), { fallback: <Loading /> }),
          },
          {
            label: '灰度功能管理',
            href: '/sysadmin/features',
            key: 'sysadmin-features',
            perm: oneOf(perms.GLOBAL_RULE_MANAGE),
            component: loadable(() => import(/* webpackChunkName: 'sysadmin' */ /* webpackMode: 'lazy' */ '@/pages/sysadmin'), { fallback: <Loading /> }),
          },
          {
            label: '标签管理',
            href: '/sysadmin/label',
            key: 'sysadmin-label',
            perm: oneOf(perms.GLOBAL_RULE_MANAGE),
            component: loadable(() => import(/* webpackChunkName: 'sysadmin' */ /* webpackMode: 'lazy' */ '@/pages/sysadmin'), { fallback: <Loading /> }),
          },
          {
            label: '活动管理',
            href: '/sysadmin/activity',
            key: 'sysadmin-activity',
            perm: oneOf(perms.ORGANIZATION_ACTIVITY_MANAGE_VIEW),
            component: loadable(() => import(/* webpackChunkName: 'sysadmin' */ /* webpackMode: 'lazy' */ '@/pages/sysadmin/ActivityManagers'), { fallback: <Loading /> }),
          },
          {
            label: '素材管理',
            href: '/sysadmin/material-list/material',
            key: 'sysadmin-material',
            data: [{
              label: '素材管理',
              key: 'material-list',
              href: '/sysadmin/material-list/material',
              component: loadable(() => import(/* webpackChunkName: 'sysadmin' */ /* webpackMode: 'lazy' */ '@/pages/agency/manage/material/list'), { fallback: <Loading /> }),
              basePage: true,
              perm: (loginStatus: Record<string, any>) => {
                return isOa() && loginStatus.oneOf([perms.ORGANIZATION_MATERIAL_VIEW, perms.ORGANIZATION_MATERIAL_EDIT]);
              },
              params: {
                isOfficial: true,
              },
            }, {
              label: '素材详情',
              key: 'material-info',
              href: '/sysadmin/material-info',
              component: loadable(() => import(/* webpackChunkName: 'sysadmin' */ /* webpackMode: 'lazy' */ '@/pages/agency/manage/material/info'), { fallback: <Loading /> }),
              basePage: true,
              hide: true,
              perm: (loginStatus: Record<string, any>) => {
                return isOa() && loginStatus.oneOf([perms.ORGANIZATION_MATERIAL_VIEW, perms.ORGANIZATION_MATERIAL_EDIT]);
              },
              params: {
                isOfficial: true,
              },
            }, {
              label: '编辑素材',
              key: 'material-edit',
              href: '/sysadmin/material-edit',
              component: loadable(() => import(/* webpackChunkName: 'sysadmin' */ /* webpackMode: 'lazy' */ '@/pages/agency/manage/material/edit'), { fallback: <Loading /> }),
              basePage: true,
              hide: true,
              perm: (loginStatus: Record<string, any>) => {
                return isOa() && loginStatus.oneOf([perms.ORGANIZATION_MATERIAL_EDIT]);
              },
              params: {
                isOfficial: true,
              },
            }],
          },
          {
            label: '呼叫中心管理',
            href: '/callcenter/config',
            perm: (loginStatus: Record<string, any>) => {
              return isOa() && loginStatus.oneOf([perms.PERM_CALL_CENTER_MANAGER]);
            },
            component: loadable(() => import(/* webpackChunkName: 'sysadmin' */ /* webpackMode: 'lazy' */ '@/pages/sysadmin/callcenterConfig'), { fallback: <Loading /> }),
          },
        ],
      },
    ],
  },
];
